import { IconArrowBackUp, IconLoader2, IconSend, IconX } from "@tabler/icons-react"
import { useState } from "react"

import { cn } from "../utilities/tailwind"

type Props = {
	value: string
	onChange(value: string): void
	onSubmit(): Promise<void>
	label: string
	submitLabel: string
	sendingLabel: string
	errorLabel: string
	isSending?: boolean
	replyTo?: string
	className?: string
} & ({ onCancel(): void; cancelLabel: string } | { onCancel?: never; cancelLabel?: never })

export default function CommunityCommentComposer({
	value,
	onChange,
	onSubmit,
	label,
	submitLabel,
	sendingLabel,
	errorLabel,
	isSending = false,
	replyTo,
	cancelLabel,
	onCancel,
	className,
}: Props) {
	const [isSubmitting, setSubmitting] = useState(false)
	const [error, setError] = useState(false)
	const busy = isSubmitting || isSending
	return (
		<form
			className={cn(
				"border-light/20 focus-within:border-primary/50 focus-within:ring-primary/10 overflow-hidden rounded-xl border bg-white shadow-sm transition-shadow focus-within:ring-2",
				className,
			)}
			onSubmit={async event => {
				event.preventDefault()
				if (busy || !value.trim()) return
				setSubmitting(true)
				setError(false)
				try {
					await onSubmit()
				} catch {
					setError(true)
				} finally {
					setSubmitting(false)
				}
			}}
		>
			{replyTo && (
				<div className="flex items-center justify-between gap-2 px-3 pt-2.5">
					<span className="text-light flex min-w-0 items-center gap-1.5 text-xs">
						<IconArrowBackUp className="size-3.5 shrink-0" />
						<span className="truncate">{replyTo}</span>
					</span>
					{onCancel && (
						<button
							type="button"
							aria-label={cancelLabel}
							disabled={busy}
							onClick={onCancel}
							className="text-light hover:bg-primary/5 hover:text-primary flex size-6 shrink-0 cursor-pointer items-center justify-center rounded-md transition-colors disabled:opacity-50"
						>
							<IconX className="size-4" />
						</button>
					)}
				</div>
			)}
			<textarea
				autoFocus={replyTo !== undefined}
				required
				aria-label={label}
				placeholder={replyTo ? undefined : label}
				value={value}
				disabled={busy}
				onChange={event => onChange(event.target.value)}
				onKeyDown={event => {
					if (event.key === "Escape" && onCancel) {
						event.stopPropagation()
						if (!busy) onCancel()
					}
				}}
				className={cn(
					"placeholder:text-light block min-h-20 w-full resize-y border-0 bg-transparent px-3 py-2 text-sm leading-6 outline-none",
					!replyTo && "pt-3",
				)}
				rows={2}
			/>
			{error && (
				<p role="alert" className="text-danger px-3 pb-2 text-xs">
					{errorLabel}
				</p>
			)}
			<div className="flex flex-wrap items-center justify-end gap-2 px-3 pb-3">
				{onCancel && (
					<button
						type="button"
						disabled={busy}
						className="text-light hover:bg-primary/5 cursor-pointer rounded-lg px-3 py-2 text-xs font-medium transition-colors disabled:opacity-50"
						onClick={onCancel}
					>
						{cancelLabel}
					</button>
				)}
				<button
					type="submit"
					disabled={busy || !value.trim()}
					className="bg-primary inline-flex cursor-pointer items-center justify-center gap-2 rounded-lg px-3 py-2 text-xs font-medium text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
				>
					{busy ? <IconLoader2 className="size-4 animate-spin" /> : <IconSend className="size-4" />}
					<span aria-live="polite">{busy ? sendingLabel : submitLabel}</span>
				</button>
			</div>
		</form>
	)
}
